import { useEffect, useState, useCallback, Fragment } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import { supabase } from '@/lib/supabaseClient';
import { logAudit } from '@/lib/logAudit';

// ── Types ─────────────────────────────────────────────────────────────────────
interface DomainRequest {
  id: string;
  tenant_id: string;
  requested_domain: string;
  status: 'pending' | 'approved' | 'rejected' | 'active' | 'cancelled';
  admin_notes: string | null;
  price_monthly: number;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  // joined
  tenant_name?: string;
  tenant_slug?: string;
  tenant_plan?: string;
  tenant_db_id?: string;
}

type FilterStatus = 'all' | 'pending' | 'approved' | 'rejected' | 'active' | 'cancelled';

// ── Constants ─────────────────────────────────────────────────────────────────
const SW = { background: '#ffffff', color: '#0f172a' };

const STATUS_CFG: Record<string, { label: string; color: string; icon: string; dot: string }> = {
  pending:   { label: 'Pending',   color: 'bg-amber-50 text-amber-700 border-amber-200',    icon: 'ri-time-line',            dot: 'bg-amber-400' },
  approved:  { label: 'Approved',  color: 'bg-sky-50 text-sky-700 border-sky-200',          icon: 'ri-checkbox-circle-line', dot: 'bg-sky-400' },
  active:    { label: 'Active',    color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'ri-global-line',         dot: 'bg-emerald-400' },
  rejected:  { label: 'Rejected',  color: 'bg-red-50 text-red-600 border-red-200',          icon: 'ri-close-circle-line',    dot: 'bg-red-400' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500 border-slate-200',   icon: 'ri-prohibited-line',      dot: 'bg-slate-300' },
};

const PLAN_BADGE: Record<string, string> = {
  free:       'bg-slate-100 text-slate-500',
  pro:        'bg-amber-50 text-amber-700',
  enterprise: 'bg-emerald-50 text-emerald-700',
};

const FILTER_TABS: { value: FilterStatus; label: string }[] = [
  { value: 'all',       label: 'Todos' },
  { value: 'pending',   label: 'Pending' },
  { value: 'approved',  label: 'Approved' },
  { value: 'active',    label: 'Active' },
  { value: 'rejected',  label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

const DEFAULT_DOMAIN_PRICE = 5;

// ── Page ──────────────────────────────────────────────────────────────────────
export default function SADomainRequests() {
  const navigate = useNavigate();

  const [requests, setRequests]         = useState<DomainRequest[]>([]);
  const [loading, setLoading]           = useState(true);
  const [filter, setFilter]             = useState<FilterStatus>('pending');
  const [search, setSearch]             = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [expandedId, setExpandedId]     = useState<string | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    const [reqRes, tenantRes] = await Promise.all([
      supabase.from('tenant_domain_requests').select('*').order('created_at', { ascending: false }),
      supabase.from('saas_tenants').select('id, name, slug, plan_code'),
    ]);

    const tenantMap: Record<string, { name: string; slug: string; plan_code: string }> = {};
    (tenantRes.data ?? []).forEach((t) => { tenantMap[t.id] = t; });

    const enriched: DomainRequest[] = ((reqRes.data ?? []) as DomainRequest[]).map((r) => ({
      ...r,
      tenant_name:  tenantMap[r.tenant_id]?.name,
      tenant_slug:  tenantMap[r.tenant_id]?.slug,
      tenant_plan:  tenantMap[r.tenant_id]?.plan_code,
      tenant_db_id: r.tenant_id,
    }));

    setRequests(enriched);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      fetchData();
    };
    init();
  }, [navigate, fetchData]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const byStatus      = (s: string) => requests.filter((r) => r.status === s).length;
  const pendingCount  = byStatus('pending');

  const filtered = requests.filter((r) => {
    const matchStatus = filter === 'all' || r.status === filter;
    const q = search.trim().toLowerCase();
    const matchSearch = !q
      || r.requested_domain.toLowerCase().includes(q)
      || (r.tenant_name ?? '').toLowerCase().includes(q)
      || (r.tenant_slug ?? '').toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  // ── Approve ───────────────────────────────────────────────────────────────
  const handleApprove = async (req: DomainRequest) => {
    const { value: priceStr, isConfirmed } = await Swal.fire({
      title: 'Aprobar dominio',
      html: `
        <div style="text-align:left;padding:4px 0">
          <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:12px 14px;margin-bottom:14px">
            <p style="color:#166534;font-size:12px;font-weight:700;margin:0 0 6px">Cambios que se aplicarán:</p>
            <p style="color:#15803d;font-size:12px;margin:2px 0"><code style="background:#dcfce7;padding:1px 6px;border-radius:4px">custom_domain</code> = <strong>${req.requested_domain}</strong></p>
            <p style="color:#15803d;font-size:12px;margin:2px 0"><code style="background:#dcfce7;padding:1px 6px;border-radius:4px">custom_domain_status</code> = <strong>pending_dns</strong></p>
          </div>
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px;margin-bottom:12px;display:flex;align-items:center;gap:10px">
            <div style="width:32px;height:32px;background:#eff6ff;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0">
              <i class="ri-building-2-line" style="color:#3b82f6"></i>
            </div>
            <div>
              <p style="color:#0f172a;font-size:13px;font-weight:600;margin:0">${req.tenant_name ?? '—'}</p>
              <p style="color:#64748b;font-size:11px;margin:2px 0 0">${req.requested_domain} · Plan: <strong style="text-transform:uppercase">${req.tenant_plan ?? 'free'}</strong></p>
            </div>
          </div>
          <p style="color:#374151;font-size:12px;font-weight:600;margin-bottom:6px">Precio mensual (USD):</p>
        </div>
      `,
      input: 'number',
      inputValue: req.price_monthly > 0 ? req.price_monthly : DEFAULT_DOMAIN_PRICE,
      inputAttributes: { min: '0', step: '1', style: 'text-align:center;font-size:22px;font-weight:700;color:#f59e0b' },
      showCancelButton: true,
      confirmButtonText: '✓ Aprobar dominio',
      cancelButtonText: 'Cancelar',
      ...SW, confirmButtonColor: '#10b981',
    });
    if (!isConfirmed) return;

    setProcessingId(req.id);
    const price = parseFloat(priceStr ?? '0') || 0;
    const now   = new Date().toISOString();

    try {
      // 1) Update saas_tenants
      const { error: tErr } = await supabase.from('saas_tenants').update({
        custom_domain: req.requested_domain,
        custom_domain_status: 'pending_dns',
        custom_domain_enabled: false,
        updated_at: now,
      }).eq('id', req.tenant_id);
      if (tErr) throw new Error(tErr.message);

      logAudit({
        tenantId: req.tenant_id,
        action: 'domain_approved',
        entity: 'domain',
        entityId: req.id,
        entityName: req.requested_domain,
        details: { price_monthly: price, tenant_name: req.tenant_name },
      }).catch(() => {});

      // 2) Update request
      const { error: rErr } = await supabase.from('tenant_domain_requests').update({
        status: 'approved',
        price_monthly: price,
        approved_at: now,
        updated_at: now,
      }).eq('id', req.id);
      if (rErr) throw new Error(rErr.message);

      setRequests((prev) => prev.map((r) =>
        r.id === req.id ? { ...r, status: 'approved', price_monthly: price, approved_at: now } : r
      ));

      Swal.fire({
        icon: 'success',
        title: 'Dominio aprobado',
        html: `
          <p style="color:#64748b;font-size:13px">
            <strong>${req.requested_domain}</strong> configurado para <strong>${req.tenant_name}</strong>.<br>
            El tenant debe configurar sus DNS para activarlo.<br>
            Precio: <strong style="color:#f59e0b">$${price}/mes</strong>
          </p>`,
        timer: 2800, showConfirmButton: false, ...SW,
      });
    } catch (err) {
      Swal.fire({ title: 'Error', text: err instanceof Error ? err.message : 'Algo salió mal.', icon: 'error', ...SW });
    }
    setProcessingId(null);
  };

  // ── Reject ────────────────────────────────────────────────────────────────
  const handleReject = async (req: DomainRequest) => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: 'Rechazar solicitud',
      html: `<p style="color:#64748b;font-size:13px;text-align:left;margin-bottom:8px">Rechazando <strong>${req.requested_domain}</strong> para <strong>${req.tenant_name ?? 'este tenant'}</strong>.</p>`,
      input: 'textarea',
      inputLabel: 'Razón (opcional, visible para el admin)',
      inputPlaceholder: 'Ej: El dominio no está disponible, ya está en uso…',
      inputAttributes: { maxlength: '400', style: 'font-size:13px;height:80px;resize:none' },
      showCancelButton: true,
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Cancelar',
      ...SW, confirmButtonColor: '#ef4444',
    });
    if (!isConfirmed) return;

    setProcessingId(req.id);
    const now = new Date().toISOString();

    try {
      // Clear domain from tenant
      const { error: tErr } = await supabase.from('saas_tenants').update({
        custom_domain: null,
        custom_domain_status: 'none',
        custom_domain_enabled: false,
        updated_at: now,
      }).eq('id', req.tenant_id);
      if (tErr) throw new Error(tErr.message);

      const { error: rErr } = await supabase.from('tenant_domain_requests').update({
        status: 'rejected',
        admin_notes: reason?.trim() || req.admin_notes,
        updated_at: now,
      }).eq('id', req.id);
      if (rErr) throw new Error(rErr.message);

      setRequests((prev) => prev.map((r) =>
        r.id === req.id ? { ...r, status: 'rejected', admin_notes: reason?.trim() || r.admin_notes } : r
      ));
    logAudit({
        tenantId: req.tenant_id,
        action: 'domain_rejected',
        entity: 'domain',
        entityId: req.id,
        entityName: req.requested_domain,
        details: { reason: reason?.trim() || null, tenant_name: req.tenant_name },
      }).catch(() => {});
    } catch (err) {
      Swal.fire({ title: 'Error', text: err instanceof Error ? err.message : 'Algo salió mal.', icon: 'error', ...SW });
    }
    setProcessingId(null);
  };

  // ── Set Active (DNS confirmed) ─────────────────────────────────────────────
  const handleSetActive = async (req: DomainRequest) => {
    const { isConfirmed } = await Swal.fire({
      title: '¿Marcar como activo?',
      html: `<p style="color:#64748b;font-size:13px">Confirma que los DNS de <strong>${req.requested_domain}</strong> están correctamente configurados y apuntando al servidor.</p>`,
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Sí, marcar activo', cancelButtonText: 'Cancelar',
      ...SW, confirmButtonColor: '#10b981',
    });
    if (!isConfirmed) return;

    setProcessingId(req.id);
    const now = new Date().toISOString();
    try {
      await supabase.from('saas_tenants').update({
        custom_domain_enabled: true,
        custom_domain_status: 'active',
        updated_at: now,
      }).eq('id', req.tenant_id);

      await supabase.from('tenant_domain_requests').update({
        status: 'active', updated_at: now,
      }).eq('id', req.id);

      setRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, status: 'active' } : r));
    logAudit({
        tenantId: req.tenant_id,
        action: 'domain_active',
        entity: 'domain',
        entityId: req.id,
        entityName: req.requested_domain,
        details: { tenant_name: req.tenant_name },
      }).catch(() => {});
    } catch (err) {
      Swal.fire({ title: 'Error', text: err instanceof Error ? err.message : 'Error.', icon: 'error', ...SW });
    }
    setProcessingId(null);
  };

  // ── Re-open ───────────────────────────────────────────────────────────────
  const handleReopen = async (req: DomainRequest) => {
    const { isConfirmed } = await Swal.fire({
      title: '¿Re-abrir solicitud?',
      text: 'La solicitud volverá a estado "pending" para revisión.',
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Sí, re-abrir', cancelButtonText: 'Cancelar', ...SW,
    });
    if (!isConfirmed) return;
    const now = new Date().toISOString();
    const { error } = await supabase.from('tenant_domain_requests')
      .update({ status: 'pending', approved_at: null, updated_at: now }).eq('id', req.id);
    if (error) { Swal.fire({ title: 'Error', text: error.message, icon: 'error', ...SW }); return; }
    setRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, status: 'pending', approved_at: null } : r));
    Swal.fire({ title: 'Solicitud reabierta', icon: 'success', timer: 1200, showConfirmButton: false, ...SW });
  };

  // ── Edit notes ────────────────────────────────────────────────────────────
  const handleEditNotes = async (req: DomainRequest) => {
    const { value: notes, isConfirmed } = await Swal.fire({
      title: 'Admin notes',
      input: 'textarea',
      inputValue: req.admin_notes ?? '',
      inputLabel: 'Nota visible para el tenant',
      inputAttributes: { maxlength: '500', style: 'font-size:13px;height:80px;resize:none' },
      showCancelButton: true, confirmButtonText: 'Guardar', cancelButtonText: 'Cancelar', ...SW,
    });
    if (!isConfirmed) return;
    const { error } = await supabase.from('tenant_domain_requests')
      .update({ admin_notes: notes?.trim() || null, updated_at: new Date().toISOString() }).eq('id', req.id);
    if (error) { Swal.fire({ title: 'Error', text: error.message, icon: 'error', ...SW }); return; }
    setRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, admin_notes: notes?.trim() || null } : r));
    Swal.fire({ title: 'Nota guardada', icon: 'success', timer: 1200, showConfirmButton: false, ...SW });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <SuperAdminLayout>
      <SAHeader
        title="Domain Requests"
        subtitle="Solicitudes de custom domain como add-on"
        actions={
          pendingCount > 0 ? (
            <span className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              {pendingCount} solicitudes pendientes
            </span>
          ) : undefined
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7">

        {loading ? <LoadingState /> : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-7">
              {[
                { label: 'Total',     value: requests.length,      icon: 'ri-global-line',          color: 'text-slate-900 bg-slate-100' },
                { label: 'Pending',   value: pendingCount,          icon: 'ri-time-line',            color: 'text-amber-700 bg-amber-50' },
                { label: 'Approved',  value: byStatus('approved'),  icon: 'ri-checkbox-circle-line', color: 'text-sky-700 bg-sky-50' },
                { label: 'Active',    value: byStatus('active'),    icon: 'ri-checkbox-circle-fill', color: 'text-emerald-700 bg-emerald-50' },
                { label: 'Rejected',  value: byStatus('rejected'),  icon: 'ri-close-circle-line',    color: 'text-red-600 bg-red-50' },
              ].map((s) => (
                <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
                  <span className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 ${s.color.split(' ')[1]}`}>
                    <i className={`${s.icon} text-base ${s.color.split(' ')[0]}`}></i>
                  </span>
                  <div>
                    <p className="text-slate-400 text-xs">{s.label}</p>
                    <p className={`text-xl font-bold ${s.color.split(' ')[0]}`}>{s.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <div className="relative min-w-[200px] flex-1 max-w-sm">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                  <i className="ri-search-line text-sm"></i>
                </span>
                <input
                  type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por dominio o tenant…"
                  className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-4 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-400 transition-colors"
                />
              </div>

              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
                {FILTER_TABS.map((tab) => {
                  const count = tab.value !== 'all' ? byStatus(tab.value) : undefined;
                  return (
                    <button
                      key={tab.value}
                      onClick={() => setFilter(tab.value)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        filter === tab.value ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900'
                      }`}
                    >
                      {tab.label}
                      {count !== undefined && count > 0 && (
                        <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold inline-flex items-center justify-center ${
                          filter === tab.value
                            ? 'bg-white/30 text-white'
                            : tab.value === 'pending' ? 'bg-amber-400 text-black' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <span className="text-slate-400 text-sm ml-auto whitespace-nowrap">
                {filtered.length} {filtered.length === 1 ? 'solicitud' : 'solicitudes'}
              </span>
            </div>

            {/* Empty state */}
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-100">
                  <i className="ri-global-line text-slate-300 text-2xl"></i>
                </div>
                <p className="text-slate-500 text-sm font-medium">
                  {filter === 'pending' ? 'Sin solicitudes pendientes' : 'Sin solicitudes para este filtro'}
                </p>
                <p className="text-slate-400 text-xs">
                  {filter === 'pending' ? 'Todos los dominios están revisados.' : 'Prueba cambiando el filtro o búsqueda.'}
                </p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        {['Tenant', 'Dominio solicitado', 'Status', 'Precio/mes', 'Solicitado', 'Acciones'].map((h) => (
                          <th key={h} className={`text-slate-400 text-xs font-semibold uppercase tracking-wider py-3.5 ${h === 'Acciones' ? 'text-right pr-5' : 'text-left px-5'}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((req) => {
                        const cfg          = STATUS_CFG[req.status] ?? STATUS_CFG.pending;
                        const planBadge    = PLAN_BADGE[req.tenant_plan ?? 'free'] ?? PLAN_BADGE.free;
                        const isProcessing = processingId === req.id;
                        const isPending    = req.status === 'pending';
                        const isApproved   = req.status === 'approved';
                        const isExpanded   = expandedId === req.id;
                        const hasNotes     = !!(req.admin_notes?.trim());

                        return (
                          <Fragment key={req.id}>
                            <tr className={`border-b border-slate-100 hover:bg-slate-50/60 transition-colors ${isExpanded ? 'bg-sky-50/20' : ''}`}>
                              {/* Tenant */}
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 text-sm font-bold shrink-0">
                                    {(req.tenant_name ?? 'T').charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    {req.tenant_db_id ? (
                                      <Link
                                        to={`/sa/tenants/${req.tenant_db_id}`}
                                        className="text-slate-800 text-sm font-semibold hover:text-blue-600 transition-colors"
                                      >
                                        {req.tenant_name ?? '—'}
                                      </Link>
                                    ) : (
                                      <p className="text-slate-800 text-sm font-semibold">{req.tenant_name ?? '—'}</p>
                                    )}
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                      <span className="text-slate-400 text-[10px] font-mono">{req.tenant_slug ?? '—'}</span>
                                      {req.tenant_plan && (
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${planBadge}`}>
                                          {req.tenant_plan}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Domain */}
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-2">
                                  <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 shrink-0">
                                    <i className="ri-global-line text-xs"></i>
                                  </span>
                                  <div>
                                    <p className="text-slate-800 text-sm font-mono font-semibold">{req.requested_domain}</p>
                                    {req.status === 'approved' && (
                                      <p className="text-sky-500 text-[10px] mt-0.5">DNS pendiente de configuración</p>
                                    )}
                                    {req.status === 'active' && (
                                      <p className="text-emerald-500 text-[10px] mt-0.5">Dominio activo y funcional</p>
                                    )}
                                  </div>
                                </div>
                              </td>

                              {/* Status */}
                              <td className="px-5 py-4">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}>
                                  <i className={`${cfg.icon} text-[10px]`}></i>
                                  {cfg.label}
                                </span>
                              </td>

                              {/* Price */}
                              <td className="px-5 py-4">
                                {req.price_monthly > 0 ? (
                                  <span className="text-amber-600 text-sm font-bold">
                                    ${Number(req.price_monthly).toFixed(0)}
                                    <span className="text-slate-400 text-xs font-normal">/mes</span>
                                  </span>
                                ) : (
                                  <span className="text-slate-300 text-sm">—</span>
                                )}
                              </td>

                              {/* Dates */}
                              <td className="px-5 py-4">
                                <p className="text-slate-500 text-xs whitespace-nowrap">
                                  {new Date(req.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' })}
                                </p>
                                {req.approved_at && (
                                  <p className="text-emerald-600/70 text-[10px] mt-0.5 whitespace-nowrap">
                                    Apr. {new Date(req.approved_at).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                                  </p>
                                )}
                              </td>

                              {/* Actions */}
                              <td className="px-5 py-4">
                                <div className="flex items-center justify-end gap-1.5">
                                  {/* Notes toggle */}
                                  {hasNotes && (
                                    <button
                                      onClick={() => setExpandedId(isExpanded ? null : req.id)}
                                      className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${isExpanded ? 'bg-sky-100 text-sky-600' : 'text-slate-400 hover:text-sky-600 hover:bg-sky-50'}`}
                                      title="Ver notas"
                                    >
                                      <i className="ri-sticky-note-line text-sm"></i>
                                    </button>
                                  )}
                                  {/* Edit notes */}
                                  <button
                                    onClick={() => handleEditNotes(req)}
                                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                                    title="Editar notas"
                                  >
                                    <i className="ri-file-text-line text-sm"></i>
                                  </button>

                                  {isPending && (
                                    <>
                                      <button
                                        onClick={() => handleApprove(req)}
                                        disabled={isProcessing}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-lg border border-emerald-200 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                                      >
                                        {isProcessing
                                          ? <span className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin"></span>
                                          : <i className="ri-check-line"></i>
                                        }
                                        Approve
                                      </button>
                                      <button
                                        onClick={() => handleReject(req)}
                                        disabled={isProcessing}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg border border-red-200 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                                      >
                                        <i className="ri-close-line"></i>
                                        Reject
                                      </button>
                                    </>
                                  )}

                                  {isApproved && (
                                    <button
                                      onClick={() => handleSetActive(req)}
                                      disabled={isProcessing}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-semibold rounded-lg border border-sky-200 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                                      title="Confirmar que DNS está configurado"
                                    >
                                      <i className="ri-global-line"></i>
                                      Set Active
                                    </button>
                                  )}

                                  {(req.status === 'rejected' || req.status === 'cancelled') && (
                                    <button
                                      onClick={() => handleReopen(req)}
                                      className="flex items-center gap-1 px-2.5 py-1 text-xs text-slate-500 hover:text-amber-700 border border-slate-200 hover:border-amber-200 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
                                    >
                                      <i className="ri-refresh-line text-xs"></i>
                                      Re-abrir
                                    </button>
                                  )}

                                  {req.status === 'active' && (
                                    <span className="text-emerald-600 text-xs font-medium flex items-center gap-1">
                                      <i className="ri-checkbox-circle-fill text-sm"></i>
                                      Activo
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {/* Expanded notes row */}
                            {isExpanded && hasNotes && (
                              <tr className="border-b border-slate-100 bg-sky-50/20">
                                <td colSpan={6} className="px-5 py-3">
                                  <div className="flex items-start gap-2.5">
                                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-sky-100 text-sky-600 shrink-0 mt-0.5">
                                      <i className="ri-sticky-note-line text-xs"></i>
                                    </span>
                                    <div>
                                      <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Admin notes</p>
                                      <p className="text-slate-600 text-sm leading-relaxed">{req.admin_notes}</p>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Footer legend */}
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
                  <p className="text-slate-400 text-xs">{filtered.length} solicitudes · {pendingCount} pendientes</p>
                  <div className="flex items-center gap-4">
                    {Object.entries(STATUS_CFG).map(([s, cfg]) => {
                      const c = byStatus(s);
                      if (c === 0) return null;
                      return (
                        <div key={s} className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>
                          <span className="text-slate-400 text-xs">{cfg.label}: {c}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Domain lifecycle info */}
            <div className="mt-5 bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-slate-400 text-xs uppercase tracking-wider font-medium mb-3">Ciclo de vida de un dominio</p>
              <div className="flex items-center gap-0 overflow-x-auto">
                {[
                  { status: 'pending',   label: 'Admin solicita',       icon: 'ri-time-line',            color: 'text-amber-700 bg-amber-50 border-amber-200' },
                  { status: 'arrow', label: '', icon: '', color: '' },
                  { status: 'approved',  label: 'SA aprueba → pending_dns', icon: 'ri-checkbox-circle-line', color: 'text-sky-700 bg-sky-50 border-sky-200' },
                  { status: 'arrow', label: '', icon: '', color: '' },
                  { status: 'active',    label: 'DNS ok → activo',      icon: 'ri-global-line',          color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
                  { status: 'arrow', label: '', icon: '', color: '' },
                  { status: 'rejected',  label: 'O rechazado',          icon: 'ri-close-circle-line',    color: 'text-red-600 bg-red-50 border-red-200' },
                ].map((step, idx) =>
                  step.status === 'arrow' ? (
                    <span key={idx} className="text-slate-300 px-2 text-lg flex-shrink-0">→</span>
                  ) : (
                    <div key={step.status} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium flex-shrink-0 ${step.color}`}>
                      <i className={`${step.icon} text-sm`}></i>
                      <span className="whitespace-nowrap">{step.label}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
}
